import React from 'react';
import {
  Typography,
  Stack,
  Checkbox,
  ListItemText,
  Autocomplete,
  TextField
} from '@mui/material';

export interface StringFilterState {
  [key: string]: {
    value: boolean;
    label?: string;
  };
}

export const stringFilterToArray = (filterState: StringFilterState) => {
  const filters = filterState as StringFilterState;
  return Object.keys(filters).filter((key) => filters[key].value);
};

interface StringFilterProps {
  title: string;
  allSelectedTitle: string;
  groupLabel?: string;
  state: StringFilterState;
  setState: React.Dispatch<React.SetStateAction<StringFilterState>>;
  sortAlphabetically?: boolean;
}

const StringFilter: React.FC<StringFilterProps> = ({
  title,
  allSelectedTitle,
  groupLabel,
  state,
  setState,
  sortAlphabetically
}) => {
  const areAllFiltersSelected = Object.keys(state).length > 0 && Object.keys(state).every((k) => state[k]?.value);

  const handleAutocompleteChange = (event: React.SyntheticEvent, newValue: string[], reason: string, details?: any) => {

    if (details?.option === 'SELECT_ALL') {
      const isSelectingAll = !areAllFiltersSelected;
      setState((prev) => {
        const nextState = { ...prev };
        Object.keys(nextState).forEach((key) => {
          nextState[key] = { ...nextState[key], value: isSelectingAll };
        });
        return nextState;
      });
      return;
    }

    setState((previousState) => {
      const newStringFilterState: StringFilterState = {};
      Object.keys(state).forEach((key) => {
        newStringFilterState[key] = { ...previousState[key], value: false };
      });
      const selectedFields = newValue.filter(v => v !== 'SELECT_ALL');
      for (const field of selectedFields) {
        newStringFilterState[field] = { ...previousState[field], value: true };
      }
      return newStringFilterState;
    });
  };

  const sortedKeys = sortAlphabetically ? Object.keys(state).sort((a, b) => {
    const labelA = state[a]?.label ?? a;
    const labelB = state[b]?.label ?? b;
    return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
  }) : Object.keys(state);
  const options = ['SELECT_ALL', ...sortedKeys];
  const selectedValues = stringFilterToArray(state);

  return (
    <Stack spacing={2} sx={{ width: '280px' }}>
      <Typography
        sx={{
          color: 'text.primary',
          fontSize: '16px',
          fontWeight: 'bold'
        }}
      >
        {title}
      </Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        value={selectedValues}
        onChange={handleAutocompleteChange}
        getOptionLabel={(option) => {
          if (option === 'SELECT_ALL') return areAllFiltersSelected ? 'Deselect All' : 'Select All';
          return state[option]?.label ?? option;
        }}
        renderOption={(props, option, { selected }) => {
          const isSelectAllOption = option === 'SELECT_ALL';
          const isChecked = isSelectAllOption ? areAllFiltersSelected : selected;

          return (
            <li {...props} key={option}>
              <Checkbox checked={isChecked} style={{ marginRight: 8, padding: 0 }} />
              <ListItemText
                primary={isSelectAllOption ? (areAllFiltersSelected ? 'Deselect All' : 'Select All') : (state[option]?.label ?? option)}
                primaryTypographyProps={{ fontWeight: isSelectAllOption ? 'bold' : 'normal' }}
              />
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={groupLabel}
            placeholder={`Search ${title.toLowerCase()}`}
          />
        )}
        renderTags={(selected) => {
          if (areAllFiltersSelected) {
            return <Typography sx={{ padding: '6px' }}>{allSelectedTitle}</Typography>;
          }
          return (
            <Typography noWrap sx={{ padding: '6px', maxWidth: '380px' }}>
              {selected.map(field => state[field]?.label ?? field).join(', ')}
            </Typography>
          );
        }}
        sx={{
          maxWidth: '430px'
        }}
      />
    </Stack>
  );
};

export default StringFilter;
